import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import Select from 'react-select'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import { completeRegistration, checkUsername, checkMobileAvailability, checkBankAccountAvailability, normalizeThaiMobile } from '../services/registrationService'
import { fetchAvailableBanks, toBankOptions, type BankOption } from '../services/bankService'
import { getThemeById } from '../config/themes'
import {
  clearRegisterCompleteFlowState,
  isValidModernRegisterCompleteFlowState,
  resolveModernRegisterCompleteFlowState,
  type ModernRegisterCompleteFlowState,
} from '@huay/shared/registerFlowState'
import './HtdAuth.css'

type CompletePageState = ModernRegisterCompleteFlowState

interface ModernRegisterCompletePageProps {
  themeConfig: TenantThemeConfig | null
}

// react-select: แสดงไอคอนธนาคารใน dropdown และช่องที่เลือก (เหมือน user-classic)
const CustomBankOption = (props: { data: BankOption; innerProps: React.HTMLAttributes<HTMLDivElement>; innerRef: React.Ref<HTMLDivElement>; isSelected: boolean; isFocused: boolean }) => {
  const { data, innerProps, innerRef, isSelected, isFocused } = props
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        ...(innerProps.style as React.CSSProperties),
        display: 'flex',
        alignItems: 'center',
        padding: '10px 15px',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#e3f2fd' : isFocused ? '#f5f5f5' : 'white',
        borderBottom: '1px solid #eee',
      }}
    >
      {data.imageUrl && (
        <img
          src={data.imageUrl}
          alt={data.label}
          style={{ width: 24, height: 24, marginRight: 12, objectFit: 'contain', borderRadius: 4 }}
        />
      )}
      <span style={{ fontSize: 14, color: '#333', fontWeight: isSelected ? 500 : 400 }}>{data.label}</span>
    </div>
  )
}

const CustomBankSingleValue = (props: { data: BankOption }) => {
  const { data } = props
  return (
    <div style={{ display: 'flex', alignItems: 'center', fontSize: 14, color: '#333', height: '100%', lineHeight: 1 }}>
      {data.imageUrl && (
        <img
          src={data.imageUrl}
          alt={data.label}
          style={{ width: 20, height: 20, marginRight: 8, objectFit: 'contain', borderRadius: 3, verticalAlign: 'middle', display: 'inline-block' }}
        />
      )}
      <span style={{ verticalAlign: 'middle', display: 'inline-block', lineHeight: 20 }}>{data.label}</span>
    </div>
  )
}

const bankSelectStyles: Record<string, unknown> = {
  container: (base: Record<string, unknown>) => ({ ...base, width: '100%', height: 48, minHeight: 0 }),
  control: (base: Record<string, unknown>, state: { isFocused: boolean }) => ({
    ...base,
    border: state.isFocused ? '1px solid var(--htd-primary)' : '1px solid #dbe3ef',
    borderRadius: 12,
    minHeight: 48,
    height: 48,
    maxHeight: 48,
    paddingLeft: '1.6rem',
    backgroundColor: state.isFocused ? '#fff' : '#f8fafc',
    boxShadow: state.isFocused ? '0 0 0 3px color-mix(in srgb, var(--htd-primary) 18%, transparent)' : 'none',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: '#6c757d',
    fontSize: 14,
    margin: 0,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0,
    padding: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e0e0e0',
    marginTop: 4,
    minWidth: 300,
    width: 'auto',
  }),
  menuList: (base: Record<string, unknown>) => ({ ...base, padding: '4px 0', maxHeight: 200 }),
  option: (base: Record<string, unknown>, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...base,
    backgroundColor: state.isSelected ? '#e3f2fd' : state.isFocused ? '#f5f5f5' : 'white',
    color: '#333',
    fontSize: 14,
    padding: '10px 15px',
  }),
  singleValue: (base: Record<string, unknown>) => ({
    ...base,
    color: '#333',
    fontSize: 14,
    margin: 0,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    padding: '0 8px',
    height: '100%',
    flexWrap: 'nowrap',
    position: 'relative',
  }),
}

/**
 * หน้า Complete การสมัคร (STEP 3 ตาม register1.html)
 * รองรับ 2 แบบ: มาจาก OTP (จาก user-classic flow) → กรอกรหัสผ่าน + ข้อมูลบัญชี | มาจาก passcode (เดิม) → ใช้รหัสจาก state
 */
const ModernRegisterCompletePage: React.FC<ModernRegisterCompletePageProps> = ({ themeConfig }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const flowState = useMemo(
    () => resolveModernRegisterCompleteFlowState(location.state as CompletePageState | null),
    [location.state]
  )

  const fromOtp = flowState?.fromOtp === true
  const fromPasscode = flowState?.fromPasscode === true

  const phone = flowState?.phone ? normalizeThaiMobile(flowState.phone) : ''
  const sessionId = flowState?.sessionId ?? ''
  const passwordFromState = flowState?.password ?? ''

  const [usernameInput, setUsernameInput] = useState(() =>
    flowState?.username && flowState.username.trim() ? flowState.username.trim() : ''
  )
  const [fullName, setFullName] = useState('')
  const [bankCode, setBankCode] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [passwordInput, setPasswordInput] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [fullNameError, setFullNameError] = useState('')
  const [bankError, setBankError] = useState('')
  const [accountError, setAccountError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [usernameError, setUsernameError] = useState('')
  const [isCheckingUsername, setIsCheckingUsername] = useState(false)
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [bankOptions, setBankOptions] = useState<BankOption[]>([])
  const [bankImageUrl, setBankImageUrl] = useState('')
  const [isLoadingBanks, setIsLoadingBanks] = useState(false)
  const [mobileTaken, setMobileTaken] = useState(false)
  const [bankAccountTaken, setBankAccountTaken] = useState(false)
  const [bankAccountCheckMessage, setBankAccountCheckMessage] = useState('')
  const [isCheckingBankAccount, setIsCheckingBankAccount] = useState(false)

  const username = (usernameInput && usernameInput.trim()) ? usernameInput.trim().toLowerCase() : phone

  // เมื่อรหัสผ่านกับยืนยันรหัสผ่านตรงกัน (เหมือน user-classic)
  const confirmPasswordMatch =
    !!passwordInput &&
    !!confirmPassword &&
    passwordInput === confirmPassword &&
    !fromPasscode

  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/', { replace: true })
      return
    }
  }, [navigate])

  useEffect(() => {
    document.body.classList.add('htd-auth-active')
    return () => document.body.classList.remove('htd-auth-active')
  }, [])

  const bgUrl = useMemo(() => {
    if (!themeConfig) return ''
    const folder = getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue'
    const cdn =
      (import.meta.env as { VITE_CDN_URL?: string }).VITE_CDN_URL ||
      'https://huay-cdn-assets-prod.s3.ap-southeast-1.amazonaws.com'
    return `${cdn}/${folder}/img/bg.jpg`
  }, [themeConfig])

  // ห้ามเข้าหน้านี้โดยไม่ผ่าน confirm OTP — ต้องมี state จากหน้า register หลังยืนยัน OTP (หรือจาก passcode)
  useEffect(() => {
    if (!isValidModernRegisterCompleteFlowState(flowState)) {
      clearRegisterCompleteFlowState()
      navigate('/register', { replace: true })
    }
  }, [flowState, navigate])

  // ตรวจสอบชื่อผู้ใช้แบบ debounce (เหมือน user-classic)
  const checkUsernameAvailability = useCallback(async (raw: string) => {
    if (!themeConfig?.tenantId || !raw || raw.trim().length < 6) {
      setUsernameAvailable(null)
      setUsernameSuggestions([])
      setShowSuggestions(false)
      return
    }
    if (!/^[a-zA-Z0-9]+$/.test(raw.trim())) {
      setUsernameAvailable(false)
      setUsernameSuggestions([])
      setShowSuggestions(false)
      return
    }
    setIsCheckingUsername(true)
    setUsernameAvailable(null)
    try {
      const res = await checkUsername(themeConfig.tenantId, raw.trim())
      setUsernameAvailable(res.available === true ? true : false)
      setUsernameSuggestions(res.suggestions || [])
      setShowSuggestions(!res.available && (res.suggestions?.length ?? 0) > 0)
    } catch {
      setUsernameAvailable(false)
      setUsernameSuggestions([])
      setShowSuggestions(false)
    } finally {
      setIsCheckingUsername(false)
    }
  }, [themeConfig?.tenantId])

  useEffect(() => {
    if (!usernameInput || usernameInput.trim().length < 6) {
      setUsernameAvailable(null)
      setUsernameSuggestions([])
      setShowSuggestions(false)
      return
    }
    const t = setTimeout(() => {
      checkUsernameAvailability(usernameInput)
    }, 500)
    return () => clearTimeout(t)
  }, [usernameInput, checkUsernameAvailability])

  // โหลดรายการธนาคารจาก tenant/API (เหมือน user-classic)
  useEffect(() => {
    const tid = themeConfig?.tenantId
    if (!tid) return
    let cancelled = false
    setIsLoadingBanks(true)
    fetchAvailableBanks(tid)
      .then((banks) => {
        if (!cancelled) setBankOptions(toBankOptions(banks))
      })
      .catch(() => {
        if (!cancelled) setBankOptions([])
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBanks(false)
      })
    return () => {
      cancelled = true
    }
  }, [themeConfig?.tenantId])

  // เช็คเบอร์โทรว่าสมัครแล้วหรือยัง (ซ้ำในระบบ = disable ปุ่ม)
  useEffect(() => {
    const tid = themeConfig?.tenantId
    if (!tid || !phone) return
    let cancelled = false
    checkMobileAvailability(tid, phone).then((r) => {
      if (!cancelled) setMobileTaken(r.success && !r.available)
    })
    return () => { cancelled = true }
  }, [themeConfig?.tenantId, phone])

  // เช็คคู่ธนาคาร+หมายเลขบัญชีว่าถูกใช้แล้วหรือยัง (debounce)
  useEffect(() => {
    const tid = themeConfig?.tenantId
    const code = (bankCode || '').trim()
    const num = (accountNumber || '').replace(/\D/g, '')
    if (!tid || !code || num.length < 10 || num.length > 12) {
      setBankAccountTaken(false)
      setBankAccountCheckMessage('')
      setBankError('')
      return
    }
    let cancelled = false
    setIsCheckingBankAccount(true)
    const t = setTimeout(() => {
      checkBankAccountAvailability(tid, code, num).then((r) => {
        if (!cancelled) {
          const taken = r.success && !r.available
          setBankAccountTaken(taken)
          const msg = taken ? (r.message || 'เลขบัญชีนี้มีผู้ใช้งานแล้วในระบบ') : ''
          setBankAccountCheckMessage(msg)
          if (taken) setBankError(msg)
          else setBankError('')
        }
        if (!cancelled) setIsCheckingBankAccount(false)
      })
    }, 500)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [themeConfig?.tenantId, bankCode, accountNumber])

  const validate = (): boolean => {
    let ok = true
    if (fromOtp) {
      if (!usernameInput || usernameInput.trim().length < 6) {
        setUsernameError('ยูสเซอร์ต้องมีอย่างน้อย 6 อักษร')
        ok = false
      } else if (!/^[a-zA-Z0-9]+$/.test(usernameInput.trim())) {
        setUsernameError('ใช้ได้เฉพาะภาษาอังกฤษและตัวเลขเท่านั้น')
        ok = false
      } else if (usernameAvailable === false) {
        setUsernameError('ยูสเซอร์นี้มีผู้ใช้งานแล้ว')
        ok = false
      } else if (isCheckingUsername) {
        setUsernameError('กำลังตรวจสอบยูสเซอร์...')
        ok = false
      } else {
        setUsernameError('')
      }
      if (!passwordInput || passwordInput.length < 6) {
        setPasswordError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร')
        ok = false
      } else {
        setPasswordError('')
      }
      if (passwordInput !== confirmPassword) {
        setConfirmPasswordError('รหัสผ่านไม่ตรงกัน')
        ok = false
      } else {
        setConfirmPasswordError('')
      }
    }
    if (!fullName.trim()) {
      setFullNameError('กรุณากรอกชื่อ-นามสกุล')
      ok = false
    } else {
      setFullNameError('')
    }
    if (!bankCode || !bankCode.trim()) {
      setBankError('กรุณาเลือกธนาคาร')
      ok = false
    } else {
      setBankError('')
    }
    const acc = accountNumber.replace(/\D/g, '')
    if (!acc || acc.length < 10) {
      setAccountError('กรุณากรอกหมายเลขบัญชี (ตัวเลขอย่างน้อย 10 หลัก)')
      ok = false
    } else {
      setAccountError('')
    }
    if (mobileTaken) ok = false
    if (bankAccountTaken) {
      setBankError(bankAccountCheckMessage || 'เลขบัญชีนี้มีผู้ใช้งานแล้วในระบบ')
      ok = false
    }
    return ok
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const pwd = fromOtp ? passwordInput : passwordFromState
    if (!themeConfig?.tenantId || !phone || !sessionId || !pwd) {
      navigate('/register', { replace: true })
      return
    }
    if (!validate()) return

    setSubmitting(true)
    try {
      const referralCode =
        typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('affiliate_referral_code') : null

      const result = await completeRegistration(
        themeConfig.tenantId,
        phone,
        pwd,
        sessionId,
        username,
        {
          fullName: fullName.trim(),
          bankCode: bankCode.trim(),
          accountNumber: accountNumber.replace(/\D/g, '').trim(),
          ...(bankImageUrl.trim() ? { bankImageUrl: bankImageUrl.trim() } : {}),
          ...(referralCode && referralCode.trim() ? { referralCode: referralCode.trim() } : {}),
        }
      )
      if (result.success) {
        clearRegisterCompleteFlowState()
        if (referralCode) {
          try {
            sessionStorage.removeItem('affiliate_referral_code')
          } catch {
            /* ignore */
          }
        }
        const loginRes = await authService.login({
          tenantId: themeConfig.tenantId,
          username,
          password: pwd,
          rememberMe: false,
        })
        if (loginRes.success) {
          await Swal.fire({
            icon: 'success',
            title: 'สมัครสมาชิกสำเร็จ',
            html: 'กำลังนำคุณเข้าสู่ระบบ',
            timer: 2000,
            showConfirmButton: false,
            timerProgressBar: true,
          })
          navigate('/dashboard', { replace: true })
        } else {
          await Swal.fire({
            icon: 'success',
            title: 'สมัครสมาชิกสำเร็จ',
            text: 'กรุณาเข้าสู่ระบบด้วยเบอร์โทร/ชื่อผู้ใช้และรหัสผ่านที่ตั้งไว้',
            confirmButtonText: 'เข้าสู่ระบบ',
          })
          navigate('/login', { replace: true })
        }
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'สมัครสมาชิกไม่สำเร็จ',
          text: result.message || 'กรุณาลองใหม่อีกครั้ง',
        })
      }
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: (e as Error)?.message ?? 'กรุณาลองใหม่อีกครั้ง',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!themeConfig || !isValidModernRegisterCompleteFlowState(flowState)) {
    return (
      <div className="htd-auth htd-auth--loading">
        <p>กำลังโหลด...</p>
      </div>
    )
  }

  const logoUrl = themeConfig.theme?.branding?.logoUrl
  const siteName = themeConfig.theme?.branding?.siteName || 'หวยทูเดย์'

  const submitDisabled =
    submitting ||
    mobileTaken ||
    (fromOtp && usernameAvailable === false) ||
    bankAccountTaken ||
    isCheckingBankAccount

  const submitLabel = submitting
    ? 'กำลังสมัครสมาชิก...'
    : mobileTaken
      ? 'เบอร์โทรนี้มีผู้ใช้แล้ว'
      : bankAccountTaken
        ? 'เลขบัญชีนี้มีผู้ใช้แล้ว'
        : fromOtp && usernameAvailable === false
          ? 'ยูสเซอร์นี้มีผู้ใช้แล้ว'
          : 'ยืนยันการสมัคร'

  return (
    <div
      className="htd-auth"
      style={bgUrl ? ({ ['--htd-auth-bg' as string]: `url(${bgUrl})` } as React.CSSProperties) : undefined}
    >
      <div className="htd-auth__ambient" aria-hidden>
        <span className="htd-auth__orb htd-auth__orb--a" />
        <span className="htd-auth__orb htd-auth__orb--b" />
      </div>

      <div className="htd-auth__shell htd-auth__shell--wide">
        <Link to="/" className="htd-auth__brand" aria-label={siteName}>
          {logoUrl ? <img src={logoUrl} alt={siteName} /> : <span>{siteName}</span>}
        </Link>

        <form onSubmit={handleSubmit} className="htd-auth__card">
          <div className="htd-auth__head">
            <span className="htd-auth__step">STEP 2</span>
            <h1 className="htd-auth__title">สมัครสมาชิก</h1>
            <p className="htd-auth__sub">ตั้งชื่อผู้ใช้ รหัสผ่าน และข้อมูลบัญชีธนาคาร</p>
          </div>

          {/* เบอร์โทรศัพท์ (ยืนยันแล้ว) */}
          <label className="htd-auth__field">
            <span>เบอร์โทรศัพท์ (ยืนยันแล้ว)</span>
            <div className="htd-auth__input-wrap">
              <i className="fas fa-mobile-alt htd-auth__lead" aria-hidden />
              <input type="text" value={phone} readOnly aria-label="เบอร์โทรศัพท์ที่ยืนยันแล้ว" />
            </div>
          </label>
          <p className={`htd-auth__hint ${mobileTaken ? 'htd-auth__hint--error' : ''}`}>
            {mobileTaken
              ? 'เบอร์โทรศัพท์นี้มีผู้ใช้งานแล้วในระบบ'
              : 'ใช้ล็อกอินได้ทั้งเบอร์หรือยูสเซอร์'}
          </p>

          {/* Username */}
          <label className="htd-auth__field">
            <span>ยูสเซอร์ (ไอดีเข้าระบบ)</span>
            <div
              className={`htd-auth__input-wrap htd-auth__input-wrap--pad${usernameError ? ' htd-auth__input-wrap--error' : ''}`}
            >
              <i className="fas fa-user htd-auth__lead" aria-hidden />
              <input
                type="text"
                value={usernameInput}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 32)
                  setUsernameInput(v)
                  setUsernameError('')
                }}
                readOnly={fromPasscode}
                disabled={submitting || fromPasscode}
                placeholder="ยูสเซอร์ผู้ใช้ (ขั้นต่ำ 6 อักษร)"
              />
              {fromOtp && isCheckingUsername && (
                <span className="spinner-border spinner-border-sm htd-auth__state" role="status" aria-hidden="true" />
              )}
              {fromOtp && !isCheckingUsername && usernameAvailable === true && (
                <i className="fas fa-check-circle htd-auth__state" style={{ color: '#16a34a' }} aria-hidden />
              )}
              {fromOtp && !isCheckingUsername && usernameAvailable === false && (
                <i className="fas fa-times-circle htd-auth__state" style={{ color: '#dc2626' }} aria-hidden />
              )}
            </div>
          </label>
          <p className={`htd-auth__hint ${usernameError ? 'htd-auth__hint--error' : ''}`}>
            {usernameError || 'ภาษาอังกฤษ/ตัวเลขเท่านั้น ขั้นต่ำ 6 อักษร'}
          </p>
          {fromOtp && showSuggestions && usernameSuggestions.length > 0 && (
            <div className="htd-auth__suggest">
              {usernameSuggestions.slice(0, 5).map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="htd-auth__suggest-btn"
                  onClick={() => {
                    setUsernameInput(s)
                    setShowSuggestions(false)
                    setUsernameSuggestions([])
                    checkUsernameAvailability(s)
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Password */}
          <label className="htd-auth__field">
            <span>รหัสผ่าน</span>
            <div className={`htd-auth__input-wrap${passwordError ? ' htd-auth__input-wrap--error' : ''}`}>
              <i className="fas fa-key htd-auth__lead" aria-hidden />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="รหัสผ่าน (ขั้นต่ำ 6 ตัวอักษร)"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value)
                  if (passwordError) setPasswordError('')
                  if (confirmPasswordError) setConfirmPasswordError('')
                }}
                disabled={submitting || fromPasscode}
                readOnly={fromPasscode}
                aria-label={fromPasscode ? 'รหัสผ่านตั้งไว้แล้ว' : 'รหัสผ่าน'}
              />
              {!fromPasscode && (
                <button
                  type="button"
                  className="htd-auth__eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                  tabIndex={-1}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                </button>
              )}
            </div>
          </label>
          {passwordError && <p className="htd-auth__hint htd-auth__hint--error">{passwordError}</p>}

          {/* Confirm password */}
          <label className="htd-auth__field">
            <span>ยืนยันรหัสผ่าน</span>
            <div
              className={`htd-auth__input-wrap${confirmPasswordError ? ' htd-auth__input-wrap--error' : confirmPasswordMatch ? ' htd-auth__input-wrap--ok' : ''}`}
            >
              <i className="fas fa-key htd-auth__lead" aria-hidden />
              <input
                type={showConfirm ? 'text' : 'password'}
                placeholder="ยืนยันรหัสผ่านอีกครั้ง"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value)
                  if (confirmPasswordError) setConfirmPasswordError('')
                }}
                disabled={submitting || fromPasscode}
                readOnly={fromPasscode}
                aria-label={fromPasscode ? 'ยืนยันรหัสผ่านตั้งไว้แล้ว' : 'ยืนยันรหัสผ่าน'}
              />
              {!fromPasscode && (
                <button
                  type="button"
                  className="htd-auth__eye"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                  tabIndex={-1}
                >
                  <i className={`fas ${showConfirm ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                </button>
              )}
            </div>
          </label>
          <p
            className={`htd-auth__hint ${confirmPasswordError ? 'htd-auth__hint--error' : confirmPasswordMatch ? 'htd-auth__hint--ok' : ''}`}
          >
            {confirmPasswordError
              ? confirmPasswordError
              : confirmPasswordMatch
                ? '✔ ยืนยันความถูกต้องของรหัสผ่านแล้ว'
                : 'กรุณาใส่รหัสผ่านยืนยันให้เหมือนกัน'}
          </p>

          <div className="htd-auth__section">ข้อมูลบัญชีธนาคาร</div>

          {/* Full name */}
          <label className="htd-auth__field">
            <span>ชื่อ-นามสกุล (เจ้าของบัญชี)</span>
            <div className={`htd-auth__input-wrap${fullNameError ? ' htd-auth__input-wrap--error' : ''}`}>
              <i className="fas fa-user-plus htd-auth__lead" aria-hidden />
              <input
                type="text"
                placeholder="ชื่อ-นามสกุล"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value)
                  if (fullNameError) setFullNameError('')
                }}
                disabled={submitting}
              />
            </div>
          </label>
          <p className={`htd-auth__hint ${fullNameError ? 'htd-auth__hint--error' : ''}`}>
            {fullNameError || 'กรอกให้ตรงกับชื่อบัญชีธนาคาร (แก้ไขไม่ได้ภายหลัง)'}
          </p>

          {/* Bank */}
          <label className="htd-auth__field">
            <span>ธนาคาร</span>
            <div className={`htd-auth__input-wrap htd-auth__select${bankError ? ' htd-auth__input-wrap--error' : ''}`}>
              <i className="fas fa-university htd-auth__lead" aria-hidden />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select
                  classNamePrefix="react-select"
                  options={bankOptions}
                  value={bankOptions.find((o) => o.value === bankCode) ?? null}
                  onChange={(opt) => {
                    if (opt) {
                      setBankCode(opt.value)
                      setBankImageUrl(opt.imageUrl || '')
                      if (bankError) setBankError('')
                    }
                  }}
                  isDisabled={submitting || isLoadingBanks}
                  placeholder={isLoadingBanks ? 'กำลังโหลดธนาคาร...' : 'เลือกธนาคาร'}
                  components={{
                    Option: CustomBankOption,
                    SingleValue: CustomBankSingleValue,
                  }}
                  styles={bankSelectStyles}
                />
              </div>
            </div>
          </label>
          {(bankError || (bankAccountTaken && bankAccountCheckMessage)) && (
            <p className="htd-auth__hint htd-auth__hint--error">{bankError || bankAccountCheckMessage}</p>
          )}
          {isCheckingBankAccount && <p className="htd-auth__hint">กำลังตรวจสอบบัญชีธนาคาร...</p>}

          {/* Account number */}
          <label className="htd-auth__field">
            <span>หมายเลขบัญชี</span>
            <div className={`htd-auth__input-wrap${accountError ? ' htd-auth__input-wrap--error' : ''}`}>
              <i className="fas fa-credit-card htd-auth__lead" aria-hidden />
              <input
                type="text"
                placeholder="หมายเลขบัญชี (ตัวเลขเท่านั้น)"
                value={accountNumber}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 15)
                  setAccountNumber(v)
                  if (accountError) setAccountError('')
                  if (bankError) setBankError('')
                }}
                disabled={submitting}
                maxLength={15}
                inputMode="numeric"
              />
            </div>
          </label>
          {accountError && <p className="htd-auth__hint htd-auth__hint--error">{accountError}</p>}

          <button type="submit" className="htd-auth__submit" disabled={submitDisabled}>
            {submitLabel}
          </button>

          <Link to="/register" className="htd-auth__back">
            <i className="fas fa-chevron-left" aria-hidden />
            กลับไปหน้าสมัครสมาชิก
          </Link>
        </form>
      </div>
    </div>
  )
}

export default ModernRegisterCompletePage
